import type { ToothBasisSeriesName } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { reportsModel, useCheckReportSignature } from '@/entities/reports';

import { SERIES_TO_OPTIONS_KEY, type ToothSliceOptions } from '../config';
import { makeToothMPRSliceOptionsRequest } from '../lib';

import { useToothFullHeadMprSliceRecords } from './useToothFullHeadMprSliceRecords';
import { useToothSliceOptions } from './useToothSliceOptions';

export const useToothSliceSettings = ({
  reportID,
  thicknessOptionsMm,
  toothID,
}: {
  reportID: string;
  thicknessOptionsMm: number[];
  toothID: string;
}) => {
  const dispatch = useAppDispatch();
  const { checkReportSignature } = useCheckReportSignature();
  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const loading = useAppSelector(
    reportsModel.selectors.selectFullHeadMprLoading(reportID),
  );
  const { sliceOptions } = useToothSliceOptions({
    thicknessOptionsMm,
    toothID,
  });
  const recordsByTileID = useToothFullHeadMprSliceRecords(toothID);
  const canChangeOptions = Boolean(
    toothID &&
      report?.ID === reportID &&
      report.YourPermissions?.CanChangeToothAssets &&
      loading === 'succeeded',
  );

  const submitSeriesOptions = async (
    seriesName: ToothBasisSeriesName,
    options: ToothSliceOptions,
  ) => {
    // NOTE: the RPC is a full replacement of all three series, so concurrent
    // submits finishing out of order would resurrect stale values — writes for
    // the tooth are serialized through a queue.
    await dispatch(
      reportsModel.thunks.setToothMPRSliceOptions({
        ...makeToothMPRSliceOptionsRequest({
          optionsBySeries: { ...sliceOptions, [seriesName]: options },
          toothID,
        }),
        series: SERIES_TO_OPTIONS_KEY[seriesName],
      }),
    );
  };

  const setSeriesOptions = (
    seriesName: ToothBasisSeriesName,
    options: ToothSliceOptions,
  ) => {
    const currentOptions = sliceOptions[seriesName];

    if (
      !canChangeOptions ||
      (currentOptions.sliceStepMm === options.sliceStepMm &&
        currentOptions.thicknessMm === options.thicknessMm)
    ) {
      return;
    }

    checkReportSignature({
      toothID,
      onSignatureChecked: async () => {
        await submitSeriesOptions(seriesName, options);
      },
    });
  };

  return { canChangeOptions, recordsByTileID, sliceOptions, setSeriesOptions };
};
